import React, { forwardRef, useLayoutEffect, useRef, useState } from "react";

import { SocialShareCardProps } from "./types";
import { SvgText } from "./common";
import { calculateStatsXPos } from "./util";
import { SOCIAL_SHARE_CARD } from "./constants";

// Root card component

const POSITION = {
    IMAGE: { X: 48, Y: 188, SIZE: 32 },
    NAME: { X: 88, Y: 214 },
    TAG: { HEIGHT: 48, PADDING_X: 16, PADDING_TEXT: 14, RADIUS: 24, Y: 180 }
};

const STATS = {
    MAX_COUNT: 3,
    LABEL_Y: 572.4,
    VALUE_Y: 615.2
};

const PNL = {
    X: 48,
    Y: 413
};

const OVERLAY = {
    X: 705,
    Y: 180,
    WIDTH: 500,
    HEIGHT: 500
};

export const SocialShareCard = forwardRef<HTMLDivElement, SocialShareCardProps>((props, ref) => {
    return (
        <div
            ref={ref}
            style={{
                display: "inline-block",
                width: props.width,
                height: props.height,
                lineHeight: 0, // Remove extra whitespace below SVG
                background: "none"
            }}
        >
            <svg
                width={props.width}
                height={props.height}
                viewBox={`0 0 ${SOCIAL_SHARE_CARD.WIDTH} ${SOCIAL_SHARE_CARD.HEIGHT}`}
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <rect
                    width={SOCIAL_SHARE_CARD.WIDTH}
                    height={SOCIAL_SHARE_CARD.HEIGHT}
                    fill={props.palette.background}
                />
                <Logo {...props} />
                <Position {...props} />
                <Pnl {...props} />
                <Stats {...props} />
                <BgPattern {...props} />
                <Overlay {...props} />
                <Defs />
            </svg>
        </div>
    );
});

export function Position({ palette, position }: SocialShareCardProps) {
    const { name, label, image } = position;

    const nameRef = useRef<SVGTextElement>(null);
    const labelRef = useRef<SVGTextElement>(null);

    const [nameWidth, setNameWidth] = useState(0);
    const [labelWidth, setLabelWidth] = useState(0);

    useLayoutEffect(() => {
        if (nameRef.current) {
            setNameWidth(nameRef.current.getComputedTextLength());
        }
        if (labelRef.current) {
            setLabelWidth(labelRef.current.getComputedTextLength());
        }
    }, [name, label]);

    // Layout constants
    const GAP_AFTER_IMAGE = 12;
    const GAP_AFTER_NAME = 12;

    // Start positions
    const imageX = POSITION.IMAGE.X;
    const nameX = imageX + POSITION.IMAGE.SIZE + GAP_AFTER_IMAGE;
    const labelXStart = nameX + nameWidth + GAP_AFTER_NAME;
    const labelTagWidth = labelWidth + POSITION.TAG.PADDING_X * 2;
    const labelCenterX = labelXStart + labelTagWidth / 2;

    return (
        <>
            {/* Token image */}
            <image
                x={imageX}
                y={POSITION.IMAGE.Y}
                width={POSITION.IMAGE.SIZE}
                height={POSITION.IMAGE.SIZE}
                href={image}
                preserveAspectRatio="xMidYMid slice"
                clipPath="url(#clip_token_image)"
            />

            {/* Position name */}
            <SvgText ref={nameRef} x={nameX} y={POSITION.NAME.Y} palette={palette} variant="positionName">
                {name}
            </SvgText>

            {/* Label pill background */}
            <rect
                x={labelXStart}
                y={POSITION.TAG.Y}
                width={labelTagWidth}
                height={POSITION.TAG.HEIGHT}
                rx={POSITION.TAG.RADIUS}
                fill={palette.primary}
                fillOpacity="0.1"
            />

            {/* Label text */}
            <SvgText ref={labelRef} x={labelCenterX} y={POSITION.NAME.Y} palette={palette} variant="positionTag" center>
                {label}
            </SvgText>
        </>
    );
}
/* Displays only first 3 stats */
function Pnl({ palette, headline }: SocialShareCardProps) {
    return (
        <SvgText x={PNL.X} y={PNL.Y} palette={palette} variant="headline">
            {headline}
        </SvgText>
    );
}

/* Displays only first N stats */
function Stats({ palette, stats }: SocialShareCardProps) {
    const xPos = calculateStatsXPos(stats);
    const statsToDisplay = stats.slice(0, STATS.MAX_COUNT);

    return (
        <>
            {statsToDisplay.map(({ label, value }, i) => (
                <React.Fragment key={i}>
                    <SvgText x={xPos[i]} y={STATS.LABEL_Y} palette={palette} variant="statLabel">
                        {label}
                    </SvgText>
                    <SvgText x={xPos[i]} y={STATS.VALUE_Y} palette={palette} variant="statValue">
                        {value}
                    </SvgText>
                </React.Fragment>
            ))}
        </>
    );
}

function Overlay({ overlay }: SocialShareCardProps) {
    return (
        <g width={OVERLAY.WIDTH} height={OVERLAY.HEIGHT} transform={`matrix(1 0 0 1 ${OVERLAY.X} ${OVERLAY.Y})`}>
            {overlay}
        </g>
    );
}

function BgPattern({ palette }: SocialShareCardProps) {
    const { primary } = palette;
    return (
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M1196.86 0C1197.91 0.0378404 1198.95 0.0805401 1200 0.125977V273H786C844.55 117.887 991.767 6.28234 1165.84 0H1196.86ZM1195.92 273C1197.28 273 1198.64 273.008 1200 273.021V675H786C790.263 452.255 972.16 273 1195.92 273Z"
            fill={primary}
        />
    );
}

function Defs() {
    return (
        <defs>
            <clipPath id="clip_token_image">
                <rect x="48" y="188" width="32" height="32" rx="16" />
            </clipPath>
        </defs>
    );
}
function Logo({ palette }: SocialShareCardProps) {
    const { textPrimary } = palette;
    return (
        <>
            <path
                d="M53.6974 69.7205L57.2993 64.8631C57.5326 64.5487 58.0272 64.6795 58.079 65.0686C59.1781 73.3129 70.382 76.2642 75.3598 69.3511L86.7618 53.9748C86.936 53.7403 87.3082 53.8628 87.3082 54.1554V63.1562C87.3082 63.5297 87.1878 63.894 86.965 64.1938L80.5802 72.7889C73.8989 82.3958 58.9976 81.3893 53.5833 71.167C53.3386 70.7042 53.3853 70.1418 53.6974 69.7205Z"
                fill={textPrimary}
            />
            <path
                d="M81.4761 57.7339L77.8534 62.6182C77.6201 62.9327 77.1264 62.8019 77.0738 62.4138C75.9653 54.1996 64.8215 51.2442 59.826 58.1043L53.4164 66.7336L48.5474 73.3634C48.3743 73.6 48 73.4765 48 73.1839V64.128C48 63.7545 48.1203 63.3913 48.3422 63.0914L54.2064 55.1843C60.5373 45.0151 76.1726 45.8722 81.5943 56.2946C81.8347 56.7564 81.7861 57.3157 81.4761 57.7339Z"
                fill={textPrimary}
            />
            <path d="M117.874 76.3019H99.6478V49.8868H104.591V72.1132H117.874V76.3019Z" fill={textPrimary} />
            <path
                d="M128.87 76.9811C127.033 76.9811 125.398 76.5912 123.964 75.8113C122.53 75.0063 121.411 73.8742 120.606 72.4151C119.826 70.9308 119.436 69.1824 119.436 67.1698C119.436 65.1824 119.838 63.4465 120.643 61.9623C121.448 60.478 122.555 59.3333 123.964 58.5283C125.398 57.6981 127.033 57.283 128.87 57.283C130.706 57.283 132.341 57.6855 133.775 58.4906C135.209 59.2956 136.329 60.4528 137.134 61.9623C137.939 63.4465 138.341 65.1824 138.341 67.1698C138.341 69.1824 137.939 70.9308 137.134 72.4151C136.329 73.8742 135.209 75.0063 133.775 75.8113C132.341 76.5912 130.706 76.9811 128.87 76.9811ZM128.87 73.3585C130.329 73.3585 131.486 72.8176 132.341 71.7358C133.222 70.6289 133.662 69.1069 133.662 67.1698C133.662 65.2075 133.222 63.6855 132.341 62.6038C131.461 61.4969 130.304 60.9434 128.87 60.9434C127.436 60.9434 126.279 61.4969 125.398 62.6038C124.543 63.6855 124.115 65.2075 124.115 67.1698C124.115 69.1069 124.543 70.6289 125.398 71.7358C126.253 72.8176 127.411 73.3585 128.87 73.3585Z"
                fill={textPrimary}
            />
            <path
                d="M150.354 76.9811C148.518 76.9811 146.882 76.5912 145.449 75.8113C144.015 75.0063 142.895 73.8742 142.09 72.4151C141.31 70.9308 140.92 69.1824 140.92 67.1698C140.92 65.1824 141.323 63.4465 142.128 61.9623C142.933 60.478 144.04 59.3333 145.449 58.5283C146.882 57.6981 148.518 57.283 150.354 57.283C152.191 57.283 153.826 57.6855 155.26 58.4906C156.694 59.2956 157.813 60.4528 158.618 61.9623C159.423 63.4465 159.826 65.1824 159.826 67.1698C159.826 69.1824 159.423 70.9308 158.618 72.4151C157.813 73.8742 156.694 75.0063 155.26 75.8113C153.826 76.5912 152.191 76.9811 150.354 76.9811ZM150.354 73.3585C151.813 73.3585 152.971 72.8176 153.826 71.7358C154.706 70.6289 155.147 69.1069 155.147 67.1698C155.147 65.2075 154.706 63.6855 153.826 62.6038C152.945 61.4969 151.788 60.9434 150.354 60.9434C148.92 60.9434 147.763 61.4969 146.882 62.6038C146.027 63.6855 145.599 65.2075 145.599 67.1698C145.599 69.1069 146.027 70.6289 146.882 71.7358C147.738 72.8176 148.895 73.3585 150.354 73.3585Z"
                fill={textPrimary}
            />
            <path
                d="M163.461 84V57.8868H168.065V58.717C168.065 59.2453 168.015 59.8491 167.914 60.5283C169.197 58.3648 171.21 57.283 173.952 57.283C175.562 57.283 176.971 57.7107 178.178 58.566C179.411 59.3962 180.354 60.566 181.008 62.0755C181.688 63.5597 182.027 65.2704 182.027 67.2075C182.027 69.1447 181.662 70.8553 180.933 72.3396C180.228 73.8239 179.247 74.9686 177.989 75.7736C176.732 76.5786 175.323 76.9811 173.763 76.9811C172.304 76.9811 171.122 76.7044 170.216 76.1509C169.335 75.5975 168.568 74.805 167.914 73.7736C168.015 74.4528 168.065 75.044 168.065 75.5472V84H163.461ZM172.631 73.3585C174.04 73.3585 175.172 72.7925 176.027 71.6604C176.908 70.5031 177.348 68.9937 177.348 67.1321C177.348 65.2956 176.92 63.8113 176.065 62.6792C175.235 61.522 174.09 60.9434 172.631 60.9434C171.222 60.9434 170.078 61.5094 169.197 62.6415C168.317 63.7484 167.876 65.2453 167.876 67.1321C167.876 68.9686 168.317 70.4654 169.197 71.6226C170.078 72.7799 171.222 73.3585 172.631 73.3585Z"
                fill={textPrimary}
            />
            <path
                d="M193.004 76.9811C190.413 76.9811 188.35 76.3522 186.816 75.0943C185.281 73.8365 184.438 72.1258 184.287 69.9623H188.966C189.017 71.0692 189.382 71.9497 190.061 72.6038C190.74 73.2327 191.683 73.5472 192.891 73.5472C193.922 73.5472 194.74 73.3333 195.344 72.9057C195.973 72.4528 196.287 71.8742 196.287 71.1698C196.287 70.566 196.036 70.1006 195.533 69.7736C195.055 69.4465 194.3 69.1824 193.268 68.9811L191.042 68.5283C187.017 67.7484 185.004 65.8742 185.004 62.9057C185.004 61.8491 185.319 60.8931 185.948 60.0377C186.577 59.1824 187.457 58.5157 188.589 58.0377C189.746 57.5346 191.067 57.283 192.551 57.283C194.992 57.283 196.941 57.8742 198.4 59.0566C199.885 60.2138 200.652 61.761 200.702 63.6981H196.099C196.023 62.7925 195.671 62.0755 195.042 61.5472C194.413 60.9937 193.583 60.717 192.551 60.717C191.671 60.717 190.941 60.8931 190.363 61.2453C189.809 61.5975 189.533 62.088 189.533 62.717C189.533 63.2704 189.746 63.7107 190.174 64.0377C190.627 64.3396 191.432 64.6038 192.589 64.8302L194.816 65.283C198.816 66.0629 200.816 67.9371 200.816 70.9057C200.816 72.0629 200.489 73.1069 199.834 74.0377C199.205 74.9686 198.3 75.6981 197.117 76.2264C195.935 76.7296 194.564 76.9811 193.004 76.9811Z"
                fill={textPrimary}
            />
            <path
                d="M212.778 76.9811C209.86 76.9811 207.57 76.1006 205.91 74.3396C204.275 72.5786 203.457 70.1635 203.457 67.0943C203.457 65.1069 203.872 63.3711 204.702 61.8868C205.533 60.4025 206.677 59.2704 208.136 58.4906C209.595 57.6855 211.218 57.283 213.004 57.283C215.595 57.283 217.633 57.9748 219.117 59.3585C220.627 60.7421 221.495 62.566 221.721 64.8302H217.042C216.966 63.7233 216.577 62.805 215.872 62.0755C215.193 61.3208 214.237 60.9434 213.004 60.9434C211.595 60.9434 210.426 61.4969 209.495 62.6038C208.589 63.6855 208.136 65.195 208.136 67.1321C208.136 68.9686 208.577 70.4654 209.457 71.6226C210.338 72.7799 211.482 73.3585 212.891 73.3585C214.199 73.3585 215.218 72.9811 215.948 72.2264C216.677 71.4465 217.105 70.4654 217.231 69.283H221.91C221.583 71.6981 220.639 73.5849 219.08 74.9434C217.545 76.3019 215.444 76.9811 212.778 76.9811Z"
                fill={textPrimary}
            />
            <path
                d="M230.657 76.9811C229.575 76.9811 228.544 76.7547 227.563 76.3019C226.582 75.8239 225.789 75.1572 225.186 74.3019C224.607 73.4214 224.318 72.4025 224.318 71.2453C224.318 69.3082 224.947 67.8868 226.204 66.9811C227.487 66.0503 229.248 65.434 231.487 65.1321L234.129 64.7925C234.858 64.6918 235.374 64.5157 235.676 64.2642C236.003 63.9874 236.167 63.6101 236.167 63.1321C236.167 62.4528 235.89 61.8868 235.336 61.434C234.808 60.956 234.079 60.717 233.148 60.717C232.091 60.717 231.236 60.9937 230.582 61.5472C229.928 62.1006 229.55 62.8679 229.45 63.8491H224.695C224.846 61.8868 225.664 60.3019 227.148 59.0943C228.632 57.8868 230.544 57.283 232.884 57.283C235.626 57.283 237.626 57.9874 238.884 59.3962C240.142 60.7799 240.77 62.805 240.77 65.4717V76.3019H236.506V75.5472C236.506 75.044 236.569 74.4528 236.695 73.7736C236.167 74.7547 235.399 75.5346 234.393 76.1132C233.412 76.6918 232.167 76.9811 230.657 76.9811ZM231.903 73.5849C233.135 73.5849 234.179 73.1698 235.035 72.3396C235.89 71.5094 236.318 70.4528 236.318 69.1698V67.1698C235.94 67.522 235.211 67.7862 234.129 67.9623L232.619 68.2641C231.462 68.4654 230.569 68.7925 229.94 69.2453C229.311 69.673 228.997 70.2767 228.997 71.0566C228.997 71.8365 229.274 72.4528 229.827 72.9057C230.38 73.3585 231.072 73.5849 231.903 73.5849Z"
                fill={textPrimary}
            />
            <path d="M245.234 49.8868H249.838V76.3019H245.234V49.8868Z" fill={textPrimary} />
            <path
                d="M262.861 76.9811C261.024 76.9811 259.389 76.5912 257.955 75.8113C256.546 75.0314 255.439 73.8994 254.634 72.4151C253.854 70.9308 253.464 69.1447 253.464 67.0566C253.464 65.1447 253.854 63.4465 254.634 61.9623C255.439 60.478 256.546 59.3333 257.955 58.5283C259.364 57.6981 260.949 57.283 262.71 57.283C265.502 57.283 267.678 58.1761 269.238 59.9623C270.798 61.7484 271.578 64.1635 271.578 67.2075V68.2264H258.181C258.207 69.7107 258.647 70.9434 259.502 71.9245C260.358 72.8805 261.477 73.3585 262.861 73.3585C263.892 73.3585 264.76 73.0943 265.464 72.566C266.194 72.0126 266.634 71.2579 266.785 70.3019H271.464C271.339 71.5597 270.898 72.7044 270.144 73.7358C269.414 74.7421 268.42 75.5346 267.163 76.1132C265.905 76.6918 264.471 76.9811 262.861 76.9811ZM266.861 64.9057C266.81 63.6478 266.408 62.6415 265.653 61.8868C264.898 61.1069 263.905 60.717 262.672 60.717C261.439 60.717 260.408 61.1069 259.578 61.8868C258.747 62.6667 258.269 63.673 258.144 64.9057H266.861Z"
                fill={textPrimary}
            />
        </>
    );
}
export default SocialShareCard;
